import React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import {
  MdOutlineDashboard,
  MdManageAccounts,
  MdOutlineSettings,
  MdKeyboardArrowRight,
  MdOutlineManageHistory,
  MdClose
} from 'react-icons/md'
import { RiGitBranchLine, RiCalendarCheckLine } from 'react-icons/ri'
import { FaBolt, FaRegUser, FaBell, FaRegCheckCircle } from 'react-icons/fa'
import { GoSidebarCollapse, GoProjectTemplate } from 'react-icons/go'
import { GrOrganization } from 'react-icons/gr'
import { VscOrganization } from 'react-icons/vsc'
import { ClickContext } from '../context'
import { Button } from '../../common/components'
import { withTenant } from '../../context/tenantConfig'
import { FaListCheck } from 'react-icons/fa6'

// Styled component for the sidebar
const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: ${props => props.width - 10}px;
  height: 100vh;
  background-color: ${props => props.config.primary};
  padding: 0;
  padding-top: 100px;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.2);
  /* Above page content (so anything that does scroll sideways slides UNDER
     the sidebar instead of painting over it), below the header (999) and
     offcanvas/modals (999+). */
  z-index: 50;
  overflow-y: auto;
  transition: width 0.3s ease;

  button {
    border: none;
    background-color: transparent;
    display: flex;
    padding: 0;
    margin: ${props => (props.sidebarOpen ? '0 10px 0 auto' : 'auto')};
    margin-top: 0;

    &:focus:not(:hover),
    &:hover {
      background: transparent;
    }
  }

  /* Mobile: the always-visible, width-collapsing sidebar becomes an
     off-canvas drawer that slides in over the content, the way a native
     app's nav drawer opens — closed (off-screen) by default, slid in via
     transform when opened from the header's menu button. Desktop rules
     above are untouched; everything here only applies at mobile widths. */
  @media (max-width: 768px) {
    width: min(82vw, 300px);
    padding-top: 90px;
    z-index: 999;
    box-shadow: 4px 0 24px rgba(15, 23, 42, 0.22);
    transform: translateX(${props => (props.mobileOpen ? '0' : '-100%')});
    transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);

    button {
      margin: 0 6px 0 auto;
    }
  }

  @media (max-width: 768px) and (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

// Mobile-only dimmed backdrop behind the open drawer; tapping it closes
// the sidebar, same as tapping outside a native app's nav drawer.
const MobileScrim = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${props => (props.show ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
    z-index: 998;
  }
`

const Ul = styled.ul`
  list-style-type: none;
  padding: ${props => (props.sidebarOpen ? '5px' : '5px 7px')};
  margin: 0;
  display: flex;
  flex-direction: column;
  background-color: transparent;
  transition: all 0.2s ease;

  li span {
    display: ${props => (props.sidebarOpen ? 'block' : 'none')};
    transition: all 0.2s ease;
  }

  li a {
    padding: ${props => (props.sidebarOpen ? '4px 14px' : '4px')};
    transition: all 0.2s ease;

    svg {
      height: ${props => (props.sidebarOpen ? '18px' : '22px')};
      width: ${props => (props.sidebarOpen ? '18px' : '22px')};
      transition: all 0.2s ease;
    }
  }

  ul li {
    width: ${props => (props.sidebarOpen ? '100%' : '44px')};
    height: 42px;
    transition: all 0.2s ease;
  }
`

const Li = styled.li`
  margin-bottom: 3px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  border-radius: 10px;
  background-color: ${props =>
    props.active ? 'rgba(255,255,255,0.2)' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props =>
      props.active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)'};
    transition: all 0.2s ease;
  }

  span {
    font-size: 14.5px;
    color: #ffffff;
    font-weight: 500;
    transition: all 0.2s ease;
  }

  svg {
    color: #ffffff;
    transition: all 0.2s ease;
  }

  hr {
    color: rgba(255, 255, 255, 0.3);
    margin: 0;
  }
`

const DropdownItem = styled.div`
  margin-bottom: 3px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: ${props => (props.sidebarOpen ? '0px' : '0')};
  border-radius: 10px;
  transition: all 0.2s ease;
  cursor: pointer;
  width: ${props => (props.sidebarOpen ? '100%' : '44px')};
  background-color: transparent;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
  }

  span {
    display: ${props => (props.sidebarOpen ? 'block' : 'none')};
    transition: all 0.2s ease;
  }

  svg {
    height: ${props => (props.sidebarOpen ? '18px' : '22px')};
    width: ${props => (props.sidebarOpen ? '18px' : '22px')};
    transition: all 0.2s ease;
  }
`

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => (props.sidebarOpen ? 'space-between' : 'center')};
  width: 100%;
  padding: ${props => (props.sidebarOpen ? '0px 18px' : '0px 7px')};
  gap: 10px;
  background-color: ${props =>
    !props.isOpen && props.active ? 'rgba(255,255,255,0.2)' : 'transparent'};
  transition: all 0.2s ease;
  border-radius: 10px;
  height: 42px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
  }

  ${props =>
    props.sidebarOpen &&
    `
    .dropdown-content {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
    }`}

  svg,
  span {
    font-size: 14.5px;
    font-weight: 500;
    color: #ffffff;
    transition: all 0.2s ease;
  }

  .dropdown-arrow {
    transition: transform 0.2s ease;
    transform: ${props => (props.isOpen ? 'rotate(90deg)' : 'rotate(0deg)')};
    display: ${props => (props.sidebarOpen ? 'block' : 'none')};
  }
`

const DropdownContent = styled.div`
  max-height: ${props => (props.isOpen && props.sidebarOpen ? '300px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding-left: ${props => (props.sidebarOpen ? '15px' : '0')};

  .dropdown-list {
    padding: 4px 0;
    margin: 0;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 2px;
      border-radius: 8px;
      transition: all 0.15s ease;
      display: flex;
      align-items: center;

      a {
        padding: 9px 14px;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 10px;
        text-decoration: none;
        width: 100%;
        color: rgba(255, 255, 255, 0.72);

        span {
          font-size: 14px;
          display: ${props => (props.sidebarOpen ? 'block' : 'none')};
          color: inherit;
        }

        svg {
          height: 18px;
          width: 18px;
          color: inherit;
          opacity: 0.9;
        }
      }

      &:hover {
        background-color: rgba(255, 255, 255, 0.12);
      }
      &:hover a {
        color: #ffffff;
      }

      &.active {
        background-color: rgba(255, 255, 255, 0.16);
      }
      &.active a {
        color: #ffffff;
        font-weight: 600;
      }
      &.active::before {
        content: '';
        position: absolute;
        left: 0;
        top: 7px;
        bottom: 7px;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: #ffffff;
      }
    }
  }
`

const Label = styled.label`
  display: flex;
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: #ffffff;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 5px 20px;
  gap: 10px;
  transition: all 0.2s ease;
`

class Sidebar extends React.Component {
  static contextType = ClickContext

  state = {
    openDropdowns: {} // This will track all dropdown states
  }

  // Generic toggle function for any dropdown
  toggleDropdown = dropdownName => {
    this.setState(prevState => ({
      openDropdowns: {
        ...prevState.openDropdowns,
        [dropdownName]: !prevState.openDropdowns[dropdownName]
      }
    }))
  }

  // Check if any child routes are active
  isDropdownActive = childRoutes => {
    const currentPath = window.location.pathname
    return childRoutes.some(route => currentPath === route)
  }

  // Render dropdown menu item
  renderDropdownItem = (
    item,
    config,
    sidebarOpen,
    toggleSidebar,
    isMobile,
    closeMobileSidebar
  ) => {
    const currentPath = window.location.pathname
    const isOpen = this.state.openDropdowns[item.key] || false
    const isActive = this.isDropdownActive(
      item.children.map(child => child.path)
    )

    return (
      <DropdownItem
        key={item.key}
        config={config.theme}
        active={isActive}
        onClick={() => {
          if (sidebarOpen) {
            this.toggleDropdown(item.key)
          } else {
            toggleSidebar()
            this.toggleDropdown(item.key)
          }
        }}
        sidebarOpen={sidebarOpen}
      >
        <DropdownHeader
          isOpen={isOpen}
          sidebarOpen={sidebarOpen}
          config={config.theme}
          active={isActive}
        >
          <div className="dropdown-content">
            <item.icon size={20} title={item.title} />
            <span>{item.title}</span>
          </div>
          <MdKeyboardArrowRight size={16} className="dropdown-arrow" />
        </DropdownHeader>

        <DropdownContent
          isOpen={isOpen}
          sidebarOpen={sidebarOpen}
          config={config.theme}
        >
          <ul className="dropdown-list">
            {item.children.map(child => {
              const isChildActive = currentPath === child.path
              return (
                <li key={child.path} className={isChildActive ? 'active' : ''}>
                  <Link
                    to={child.path}
                    onClick={() => {
                      if (isMobile) closeMobileSidebar()
                    }}
                  >
                    <child.icon size={18} />
                    <span>{child.title}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </DropdownContent>
      </DropdownItem>
    )
  }

  render() {
    const currentPath = window.location.pathname
    const {
      sidebarOpen,
      toggleSidebar,
      outletMarginLeft,
      isMobile,
      isSidebarMobileOpen,
      closeMobileSidebar
    } = this.context
    const { tenant } = this.props
    const { config } = tenant
    const currentUser = JSON.parse(localStorage.getItem('user')) || {}
    const isAdmin = currentUser.role.toLowerCase() === 'admin'
    const role = String(currentUser.role || '').toLowerCase()
    const isManagerOrAdmin = role === 'admin' || role === 'manager'

    // Define your menu structure here - easy to add new dropdowns
    const menuItems = [
      {
        type: 'link',
        path: '/dashboard',
        title: 'Dashboard',
        icon: MdOutlineDashboard
      },
      {
        type: 'link',
        path: '/candidates',
        title: 'Candidates',
        icon: FaRegUser
      },
      {
        type: 'dropdown',
        key: 'actions',
        title: 'Actions',
        icon: FaBolt,
        children: [
          {
            path: '/pending-for-approval',
            title: 'Pending for Approval',
            icon: FaListCheck
          }
        ]
      },
      {
        type: 'link',
        path: '/pipelines',
        title: 'Pipelines',
        icon: RiGitBranchLine,
        iconProps: { style: { strokeWidth: '0.4px' } }
      },
      {
        // Every interview on a pipeline assigned to this user, across all
        // pipelines — regardless of who scheduled it. Admin-wide ("see
        // everyone's") visibility is a deferred enhancement — for now this is
        // scoped server-side to the logged-in user's assigned pipelines for
        // everyone, admins included.
        type: 'link',
        path: '/interviews',
        title: 'Interviews',
        icon: RiCalendarCheckLine
      },
      {
        type: 'dropdown',
        key: 'dataManagement',
        title: 'Data Management',
        icon: MdOutlineManageHistory,
        children: [
          {
            path: '/companies',
            title: 'Companies',
            icon: GrOrganization
          },
          {
            path: '/job-profiles',
            title: 'Job Profiles',
            icon: VscOrganization
          },
          {
            path: '/templates',
            title: 'Templates',
            icon: GoProjectTemplate
          },
          ...(isManagerOrAdmin
            ? [
                {
                  path: '/operational-checklists',
                  title: 'Operational Checklists',
                  icon: FaListCheck
                }
              ]
            : []),
          ...(isAdmin
            ? [
                {
                  path: '/interview-reminders',
                  title: 'Interview Reminders',
                  icon: FaBell
                },
                {
                  path: '/interview-acknowledgments',
                  title: 'Interview Acknowledgments',
                  icon: FaRegCheckCircle
                }
              ]
            : [])
        ]
      },
      {
        type: 'link',
        path: '/user-management',
        title: 'User Management',
        icon: MdManageAccounts
      },
      {
        type: 'link',
        path: '/setting',
        title: 'Setting',
        icon: MdOutlineSettings
      }
    ]

    const visibleMenuItems = menuItems.filter(function(item) {
      if (item.path === '/user-management' || item.path === '/setting') {
        return isAdmin
      }
      return true
    })

    return (
      <div>
        <MobileScrim
          show={isMobile && isSidebarMobileOpen}
          onClick={closeMobileSidebar}
        />
        <SidebarContainer
          width={outletMarginLeft}
          sidebarOpen={sidebarOpen}
          mobileOpen={isMobile && isSidebarMobileOpen}
          config={config.theme}
        >
          <Button
            type="button"
            onClick={isMobile ? closeMobileSidebar : toggleSidebar}
            aria-label={isMobile ? 'Close menu' : 'Collapse menu'}
          >
            {isMobile ? (
              <MdClose size={22} color="#ffffff" />
            ) : (
              <GoSidebarCollapse size={24} color="#ffffff" />
            )}
          </Button>

          <Ul sidebarOpen={sidebarOpen}>
            <Li
              style={{ paddingLeft: sidebarOpen ? '20px' : '0px' }}
              config={config.theme}
            >
              {sidebarOpen ? <Label>Main Menu</Label> : <hr />}
            </Li>

            <Ul sidebarOpen={sidebarOpen}>
              {visibleMenuItems.map(item => {
                if (item.type === 'dropdown') {
                  return this.renderDropdownItem(
                    item,
                    config,
                    sidebarOpen,
                    toggleSidebar,
                    isMobile,
                    closeMobileSidebar
                  )
                } else {
                  // Regular menu item
                  const isActive = currentPath === item.path
                  return (
                    <Li key={item.path} config={config.theme} active={isActive}>
                      <StyledLink
                        to={item.path}
                        onClick={() => {
                          if (isMobile) closeMobileSidebar()
                        }}
                      >
                        <item.icon
                          size={20}
                          title={item.title}
                          {...(item.iconProps || {})}
                        />
                        <span>{item.title}</span>
                      </StyledLink>
                    </Li>
                  )
                }
              })}
            </Ul>
          </Ul>
        </SidebarContainer>
      </div>
    )
  }
}

export default withTenant(Sidebar)
